/* Timbrica — /aurora-forecast ("северное сияние / northern-lights forecast").
 *
 * Aurora-HUNTING companion to /space-weather. Reuses the same same-origin NOAA
 * proxies (/api/space-weather/*) — no backend duplication — plus the shared
 * weather, light-pollution and reverse-geocode endpoints the other map tools
 * already own.
 *
 * ── What this answers that a bare oval map does not ─────────────────────────
 * A visible aurora needs FOUR things at once, and every one of them is a
 * separate data source:
 *   1. the oval overhead (or close enough to sit on the horizon) — NOAA OVATION
 *   2. a dark sky                                                — computed here
 *   3. no cloud                                                  — MET/DWD
 *   4. no moon washing out a faint glow                          — computed here
 * Consumer aurora sites print (1) and stop, which is why "Kp 5!" so often ends
 * in a disappointed trip. Everything below exists to answer all four for ONE
 * point the user chose, and to say plainly which of the four is the blocker.
 *
 * ── Two numbers, not one ────────────────────────────────────────────────────
 * "Aurora chance" is ambiguous: overhead and on-the-horizon are different
 * experiences with different thresholds. We report BOTH — the OVATION value at
 * the user's cell, and the strongest value in the poleward band that is still
 * geometrically visible from there (see HORIZON_DEG).
 *
 * ── Why geomagnetic latitude ────────────────────────────────────────────────
 * The oval is anchored to the magnetic pole, not the geographic one, which is
 * why Anchorage (61.2°N) sees far more aurora than Petersburg (59.9°N). The
 * "you need Kp ≥ N here" line is therefore computed from GEOMAGNETIC latitude
 * via the dipole approximation; the FAQ explains it.
 */
(function () {
  'use strict';

  var C = window.AURORA_CFG || {};
  var T = C.t || {}, API = C.api || {};
  var tr = function (k) { return T[k] != null ? T[k] : k; };
  var $ = function (id) { return document.getElementById(id); };
  var ae = function (ev, p) { try { window.__ae && window.__ae(ev, p || {}); } catch (e) {} };
  var stage = function (s, m) { try { window.__stage && window.__stage(s, m || {}); } catch (e) {} };
  var SLUG = 'aurora-forecast';

  function fmt(tpl, vals) {
    return String(tpl).replace(/:(\w+)/g, function (m, k) { return vals[k] != null ? vals[k] : m; });
  }

  // ════════════════════════════════════════════════════════════════════════
  //  1. Astronomy — compact SunCalc (mourner/suncalc, BSD-2) + moon
  // ════════════════════════════════════════════════════════════════════════
  var RAD = Math.PI / 180, DAY_MS = 864e5, J1970 = 2440588, J2000 = 2451545;
  var OBLIQ = RAD * 23.4397;

  function toDays(d) { return d.valueOf() / DAY_MS - 0.5 + J1970 - J2000; }
  function rightAsc(l, b) { return Math.atan2(Math.sin(l) * Math.cos(OBLIQ) - Math.tan(b) * Math.sin(OBLIQ), Math.cos(l)); }
  function decl(l, b) { return Math.asin(Math.sin(b) * Math.cos(OBLIQ) + Math.cos(b) * Math.sin(OBLIQ) * Math.sin(l)); }
  function siderealT(d, lw) { return RAD * (280.16 + 360.9856235 * d) - lw; }
  function altOf(H, phi, dec) { return Math.asin(Math.sin(phi) * Math.sin(dec) + Math.cos(phi) * Math.cos(dec) * Math.cos(H)); }
  function solarAnomaly(d) { return RAD * (357.5291 + 0.98560028 * d); }
  function eclLong(M) {
    var Cc = RAD * (1.9148 * Math.sin(M) + 0.02 * Math.sin(2 * M) + 0.0003 * Math.sin(3 * M));
    return M + Cc + RAD * 102.9372 + Math.PI;
  }
  function sunCoords(d) { var M = solarAnomaly(d), L = eclLong(M); return { dec: decl(L, 0), ra: rightAsc(L, 0) }; }
  function moonCoords(d) {
    var L = RAD * (218.316 + 13.176396 * d), M = RAD * (134.963 + 13.064993 * d), F = RAD * (93.272 + 13.229350 * d);
    var l = L + RAD * 6.289 * Math.sin(M), b = RAD * 5.128 * Math.sin(F), dt = 385001 - 20905 * Math.cos(M);
    return { ra: rightAsc(l, b), dec: decl(l, b), dist: dt };
  }
  /** Sun altitude in DEGREES at a moment/place. */
  function sunAlt(date, lat, lng) {
    var lw = RAD * -lng, phi = RAD * lat, d = toDays(date), c = sunCoords(d);
    return altOf(siderealT(d, lw) - c.ra, phi, c.dec) / RAD;
  }
  /** Moon altitude in DEGREES (refraction ignored — we only need "is it up"). */
  function moonAlt(date, lat, lng) {
    var lw = RAD * -lng, phi = RAD * lat, d = toDays(date), c = moonCoords(d);
    return altOf(siderealT(d, lw) - c.ra, phi, c.dec) / RAD;
  }
  /** Illuminated fraction 0..1 and phase 0..1 (0 = new, .5 = full). */
  function moonIllum(date) {
    var d = toDays(date), s = sunCoords(d), m = moonCoords(d), sd = 149598000;
    var phi = Math.acos(Math.sin(s.dec) * Math.sin(m.dec) + Math.cos(s.dec) * Math.cos(m.dec) * Math.cos(s.ra - m.ra));
    var inc = Math.atan2(sd * Math.sin(phi), m.dist - sd * Math.cos(phi));
    var angle = Math.atan2(Math.cos(s.dec) * Math.sin(s.ra - m.ra),
      Math.sin(s.dec) * Math.cos(m.dec) - Math.cos(s.dec) * Math.sin(m.dec) * Math.cos(s.ra - m.ra));
    return { fraction: (1 + Math.cos(inc)) / 2, phase: 0.5 + 0.5 * inc * (angle < 0 ? -1 : 1) / Math.PI };
  }
  function moonEmoji(p) { return ['🌑', '🌒', '🌓', '🌔', '🌕', '🌖', '🌗', '🌘'][Math.round(p * 8) % 8]; }

  // ── Geomagnetic latitude + "from which Kp" — public/js/geomag.js ──────────
  // Shared with /space-weather, which names the same threshold for a city; one
  // table, so the two pages cannot disagree about the same place.
  var GM = window.__geomag;
  var geomagLat = GM.lat, kpNeeded = GM.kpNeeded;

  // ════════════════════════════════════════════════════════════════════════
  //  2. OVATION grid — dense array + bilinear sampling
  // ════════════════════════════════════════════════════════════════════════
  var GW = 360, GH = 181;            // lon −180..179, lat −90..90, 1° cells
  var grid = null, gridMax = 0;

  function buildGrid(cells) {
    var g = new Float32Array(GW * GH), max = 0;
    for (var i = 0; i < cells.length; i++) {
      var c = cells[i];
      var x = ((Math.round(c[0]) + 180) % 360 + 360) % 360;
      var y = Math.round(c[1]) + 90;
      if (y < 0 || y >= GH) continue;
      g[y * GW + x] = c[2];
      if (c[2] > max) max = c[2];
    }
    grid = g; gridMax = max;
  }
  function sampleGrid(lat, lon) {
    if (!grid || lat < -90 || lat > 90) return 0;
    var fx = ((lon + 180) % 360 + 360) % 360, fy = lat + 90;
    var x0 = Math.floor(fx), y0 = Math.floor(fy), tx = fx - x0, ty = fy - y0;
    var x1 = (x0 + 1) % GW; x0 = x0 % GW;
    if (y0 < 0) { y0 = 0; ty = 0; }
    var y1 = y0 + 1 >= GH ? GH - 1 : y0 + 1;
    var a = grid[y0 * GW + x0], b = grid[y0 * GW + x1], c = grid[y1 * GW + x0], d = grid[y1 * GW + x1];
    return (a * (1 - tx) + b * tx) * (1 - ty) + (c * (1 - tx) + d * tx) * ty;
  }

  // ── How far away an aurora can still be SEEN ──────────────────────────────
  // Green emission bottoms out near 100 km. An observer looking at 1° above the
  // horizon reaches it at a ground distance of
  //     d = R·( arccos( R·cos ε / (R+h) ) − ε )
  // = 1014 km with R = 6371 km, h = 100 km, ε = 1° — i.e. 9.1° of latitude.
  // That is the whole basis of the "view line" and of the horizon chance below;
  // NOAA's own view line is built on the same ~1000 km assumption.
  var HORIZON_DEG = (function () {
    var R = 6371, h = 100, eps = 1 * RAD;
    var d = R * (Math.acos(R * Math.cos(eps) / (R + h)) - eps);
    return d / 111.32;
  })();
  var OVAL_EDGE = 5;   // OVATION value that counts as "the oval is here"

  /** Smallest |lat| on this meridian where the oval reaches OVAL_EDGE. */
  function ovalEdgeLat(hemi, lon) {
    var best = null;
    for (var a = 89; a >= 32; a -= 0.5) if (sampleGrid(hemi * a, lon) >= OVAL_EDGE) best = a;
    return best;
  }
  /** Dashed "you may see it on the horizon from here" line, per hemisphere. */
  function viewLine(hemi) {
    var pts = [];
    for (var lon = -180; lon <= 180; lon += 2) {
      var e = ovalEdgeLat(hemi, lon);
      if (e == null) { pts.push(null); continue; }
      var lat = e - HORIZON_DEG;
      pts.push(lat < 2 ? null : [hemi * lat, lon]);
    }
    return pts;
  }

  // ════════════════════════════════════════════════════════════════════════
  //  3. State
  // ════════════════════════════════════════════════════════════════════════
  var map = null, cv = null, pin = null;
  var nowData = null, auroraData = null, weatherData = null, skyData = null;
  var loc = null;                    // { lat, lon, name, tz }
  var previewTime = null;            // timeline scrubbing (null = now)
  var layers = { night: true, view: true };
  var lastVerdict = null, completeSent = false;

  function nowOrPreview() { return previewTime || new Date(); }

  // ── Clock: every time on this page belongs to the PLACE, not the visitor ──
  // "Dark from 05:45" is useless if 05:45 is the reader's own wall clock while
  // the question is about Tromsø. Checking Tromsø from Adelaide is a normal use
  // of a trip-planning tool, so the whole page renders in the target's zone and
  // says so whenever that differs from the browser's.
  var tzInfo = { kind: 'browser', offsetMin: -new Date().getTimezoneOffset(), name: '' };

  function offsetForIana(tz, date) {
    try {
      var dtf = new Intl.DateTimeFormat('en-US', {
        timeZone: tz, hour12: false, year: 'numeric', month: '2-digit',
        day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit'
      });
      var p = {};
      dtf.formatToParts(date).forEach(function (x) { p[x.type] = x.value; });
      var asUtc = Date.UTC(+p.year, +p.month - 1, +p.day, (+p.hour) % 24, +p.minute, +p.second);
      return Math.round((asUtc - date.getTime()) / 60000);
    } catch (e) { return null; }
  }
  function setTimezone(tz, lon, source) {
    var now = new Date(), off = null, kind = 'lon';
    if (tz) { off = offsetForIana(tz, now); if (off !== null) kind = 'iana'; }
    if (off === null && source === 'geo') { off = -now.getTimezoneOffset(); kind = 'browser'; }
    // Longitude → nominal solar offset. Coarse (political zones wander), but for
    // "when does it get dark here" it is right to the hour, and an hour of slop
    // is far less wrong than showing the reader's own midnight.
    if (off === null) off = Math.round(lon / 15) * 60;
    tzInfo = { kind: kind, offsetMin: off, name: tz || '' };
  }
  /** ms to add to a Date so that browser-local formatting prints PLACE time. */
  function shiftMs(d) { return (tzInfo.offsetMin + d.getTimezoneOffset()) * 60000; }
  function atPlace(d) { return new Date(d.getTime() + shiftMs(d)); }
  function tzLabel() {
    var h = tzInfo.offsetMin / 60;
    return 'UTC' + (h >= 0 ? '+' : '−') + (Math.abs(h) % 1 ? Math.abs(h).toFixed(1) : Math.abs(h));
  }
  function tzDiffersFromBrowser() {
    return tzInfo.offsetMin !== -new Date().getTimezoneOffset();
  }

  // ════════════════════════════════════════════════════════════════════════
  //  4. Map + canvas compositing
  // ════════════════════════════════════════════════════════════════════════
  // Colour ramp. Aurora is green at low energy and climbs through yellow into
  // red, so the ramp is the physical one rather than an arbitrary heat map.
  var RAMP = [
    [0, 12, 74, 56, 0.00], [3, 22, 140, 92, 0.20], [10, 53, 209, 126, 0.44],
    [20, 163, 230, 53, 0.62], [35, 251, 191, 36, 0.78], [60, 248, 113, 113, 0.90],
    [100, 239, 68, 68, 0.96]
  ];
  function rampAt(v, out) {
    if (v <= RAMP[0][0]) { out[0] = out[1] = out[2] = out[3] = 0; return; }
    for (var i = 1; i < RAMP.length; i++) {
      if (v <= RAMP[i][0] || i === RAMP.length - 1) {
        var a = RAMP[i - 1], b = RAMP[i];
        var t = (v - a[0]) / (b[0] - a[0]); if (t > 1) t = 1; if (t < 0) t = 0;
        out[0] = a[1] + (b[1] - a[1]) * t;
        out[1] = a[2] + (b[2] - a[2]) * t;
        out[2] = a[3] + (b[3] - a[3]) * t;
        out[3] = (a[4] + (b[4] - a[4]) * t) * 255;
        return;
      }
    }
  }
  /** Daylight veil opacity for a sun altitude (deg): 0 at night → .40 in day.
   *  Deliberately short of opaque — the day side has to read as "not visible
   *  from here", not as a hole punched in the map. */
  function veilAlpha(alt) {
    if (alt >= 0) return 0.40;
    if (alt <= -18) return 0;
    var t = (alt + 18) / 18;
    return 0.40 * Math.pow(t, 1.35);
  }

  var buf = null, bufCtx = null;
  function ensureBuf(w, h) {
    if (!buf) { buf = document.createElement('canvas'); bufCtx = buf.getContext('2d', { willReadFrequently: true }); }
    if (buf.width !== w || buf.height !== h) { buf.width = w; buf.height = h; }
    return bufCtx;
  }

  function initMap() {
    if (!window.L || !$('auMap')) return;
    var tile = C.tile || {};
    var home = C.home || { lat: 62, lon: 20, z: 3 };
    map = L.map('auMap', {
      center: [home.lat, home.lon], zoom: home.z, minZoom: 1, maxZoom: 8,
      worldCopyJump: true, zoomControl: false,
      // Leaflet's default wheel handler hijacks a page scroll that happens to
      // cross the map. On a page this tall that is the classic "I scrolled and
      // the map ate it" trap, so the wheel needs Ctrl/⌘ (touch pinch is fine).
      scrollWheelZoom: false,
      attributionControl: false
    });
    // Leaflet control corners are physical, not logical, so on /ar they have to
    // be mirrored by hand — otherwise the zoom buttons land on the Kp badge and
    // the attribution lands under the legend.
    var rtl = C.dir === 'rtl';
    L.control.zoom({ position: rtl ? 'topleft' : 'topright' }).addTo(map);
    L.control.attribution({ position: rtl ? 'bottomleft' : 'bottomright' }).addTo(map);
    // Подложка — общий примитив (адреса, запасной путь, честная строка при
    // отказе); сияние читается только на тёмной, поэтому яркость не выбирается.
    if (window.__mapTiles) {
      window.__mapTiles.base(L, map, null, { slug: 'aurora-forecast', style: tile.style || 'dark', credit: tr('map_attr'), opts: { maxZoom: 19 }, text: tile.offline });
    } else if (window.__basemapFallback) {
      window.__basemapFallback(L, map, tr('map_attr'));
    }

    cv = document.createElement('canvas');
    cv.className = 'au-canvas';
    map.getPanes().overlayPane.appendChild(cv);
    // `move` fires on every animation frame of a drag, and one draw is ~60 k
    // trig evaluations across two buffers. Coalescing to one per frame keeps a
    // pan smooth instead of chasing the pointer.
    map.on('move zoom resize viewreset moveend zoomend', scheduleDraw);
    map.on('click', function (e) {
      setLocation(e.latlng.lat, e.latlng.lng, null, 'map');
    });

    // Ctrl/⌘+wheel zooms; a bare wheel scrolls the page and says why once.
    var mapEl = $('auMap');
    mapEl.addEventListener('wheel', function (ev) {
      if (ev.ctrlKey || ev.metaKey) {
        ev.preventDefault();
        map.setZoom(map.getZoom() + (ev.deltaY < 0 ? 1 : -1));
      } else {
        flashHint();
      }
    }, { passive: false });
  }

  var hintT = null;
  function flashHint() {
    var el = $('auWheelHint'); if (!el) return;
    el.classList.add('on');
    clearTimeout(hintT);
    hintT = setTimeout(function () { el.classList.remove('on'); }, 1600);
  }

  var drawPending = false;
  function scheduleDraw() {
    if (drawPending) return;
    drawPending = true;
    var run = function () { drawPending = false; draw(); };
    if (window.requestAnimationFrame) window.requestAnimationFrame(run);
    else setTimeout(run, 16);
  }

  function draw() {
    if (!map || !cv) return;
    var size = map.getSize();
    if (!size.x || !size.y) return;
    var dpr = Math.min(2, window.devicePixelRatio || 1);
    cv.width = Math.round(size.x * dpr); cv.height = Math.round(size.y * dpr);
    cv.style.width = size.x + 'px'; cv.style.height = size.y + 'px';
    L.DomUtil.setPosition(cv, map.containerPointToLayerPoint([0, 0]));
    var ctx = cv.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size.x, size.y);

    // Web Mercator is separable, so one lat per row and one lon per column is
    // enough — 300 unprojects instead of 30 000.
    var STEP = 4;
    var nx = Math.ceil(size.x / STEP) + 1, ny = Math.ceil(size.y / STEP) + 1;
    var lons = new Float64Array(nx), lats = new Float64Array(ny);
    for (var i = 0; i < nx; i++) lons[i] = map.containerPointToLatLng([i * STEP, 0]).lng;
    for (var j = 0; j < ny; j++) lats[j] = map.containerPointToLatLng([0, j * STEP]).lat;

    if (grid) drawAurora(ctx, size, lons, lats, nx, ny, STEP);
    if (layers.night) drawNight(ctx, size, lons, lats, nx, ny, STEP);
    if (layers.view && grid) drawViewLines(ctx);
  }

  function drawAurora(ctx, size, lons, lats, nx, ny, STEP) {
    var b = ensureBuf(nx, ny);
    var img = b.createImageData(nx, ny), data = img.data, rgba = [0, 0, 0, 0], p = 0;
    for (var j = 0; j < ny; j++) {
      var lat = lats[j];
      for (var i = 0; i < nx; i++, p += 4) {
        var v = sampleGrid(lat, lons[i]);
        if (v < 1) continue;
        rampAt(v, rgba);
        data[p] = rgba[0]; data[p + 1] = rgba[1]; data[p + 2] = rgba[2]; data[p + 3] = rgba[3];
      }
    }
    b.putImageData(img, 0, 0);
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.imageSmoothingEnabled = true;
    try { ctx.imageSmoothingQuality = 'high'; } catch (e) {}
    // A touch of blur turns the 1° grid into a glow instead of a mosaic. Guarded:
    // `filter` is unsupported on some older WebKit, where it must degrade, not throw.
    try { ctx.filter = 'blur(' + Math.max(2, STEP * 0.9) + 'px)'; } catch (e) {}
    ctx.drawImage(buf, 0, 0, nx, ny, 0, 0, nx * STEP, ny * STEP);
    try { ctx.filter = 'none'; } catch (e) {}
    ctx.restore();
  }

  function drawNight(ctx, size, lons, lats, nx, ny, STEP) {
    var when = nowOrPreview();
    var d = toDays(when), sc = sunCoords(d);
    var sinDec = Math.sin(sc.dec), cosDec = Math.cos(sc.dec);
    var b = ensureBuf(nx, ny);
    var img = b.createImageData(nx, ny), data = img.data, p = 0;
    // Local hour angle per column, sin/cos of latitude per row.
    var H = new Float64Array(nx);
    for (var i = 0; i < nx; i++) H[i] = siderealT(d, RAD * -lons[i]) - sc.ra;
    for (var j = 0; j < ny; j++) {
      var phi = RAD * lats[j], sp = Math.sin(phi), cp = Math.cos(phi);
      for (var k = 0; k < nx; k++, p += 4) {
        var alt = Math.asin(sp * sinDec + cp * cosDec * Math.cos(H[k])) / RAD;
        var a = veilAlpha(alt);
        if (a <= 0) continue;
        data[p] = 150; data[p + 1] = 176; data[p + 2] = 210; data[p + 3] = a * 255;
      }
    }
    b.putImageData(img, 0, 0);
    ctx.save();
    ctx.imageSmoothingEnabled = true;
    try { ctx.imageSmoothingQuality = 'high'; } catch (e) {}
    ctx.drawImage(buf, 0, 0, nx, ny, 0, 0, nx * STEP, ny * STEP);
    ctx.restore();
  }

  var viewCache = { key: '', north: null, south: null };
  function drawViewLines(ctx) {
    var key = String(auroraData && auroraData.fcst) + '|' + gridMax;
    if (viewCache.key !== key) {
      viewCache = { key: key, north: viewLine(1), south: viewLine(-1) };
    }
    ctx.save();
    ctx.setLineDash([7, 5]);
    ctx.lineWidth = 1.6;
    ctx.strokeStyle = 'rgba(255,105,140,.85)';
    ctx.shadowColor = 'rgba(255,105,140,.5)';
    ctx.shadowBlur = 6;
    [viewCache.north, viewCache.south].forEach(function (pts) {
      if (!pts) return;
      // Draw the line on every visible world copy so a pan across the
      // antimeridian does not leave it hanging in empty space.
      var c = map.getCenter(), size = map.getSize();
      for (var w = -1; w <= 1; w++) {
        ctx.beginPath();
        var open = false, prev = null;
        for (var i = 0; i < pts.length; i++) {
          if (!pts[i]) { open = false; prev = null; continue; }
          var lon = pts[i][1] + w * 360 + Math.round((c.lng - pts[i][1]) / 360) * 360;
          var pt = map.latLngToContainerPoint([pts[i][0], lon]);
          // The line is sampled from lon −180 to +180; once normalised onto the
          // visible world copy, the two ends land on opposite edges. Joining
          // them drew a straight dash right across the map at a plausible
          // latitude — an artefact that looked exactly like real data.
          if (prev && Math.abs(pt.x - prev.x) > size.x * 0.5) { open = false; }
          if (!open) { ctx.moveTo(pt.x, pt.y); open = true; } else ctx.lineTo(pt.x, pt.y);
          prev = pt;
        }
        ctx.stroke();
      }
    });
    ctx.restore();
  }

  function placePin() {
    if (!map || !loc) return;
    if (pin) map.removeLayer(pin);
    var icon = L.divIcon({ className: 'au-pin', html: '<i></i><b></b>', iconSize: [22, 22], iconAnchor: [11, 11] });
    pin = L.marker([loc.lat, loc.lon], { icon: icon, keyboard: false, interactive: false }).addTo(map);
  }

  // ════════════════════════════════════════════════════════════════════════
  //  5. Data loading
  // ════════════════════════════════════════════════════════════════════════
  function setMapState(s) {
    var el = $('auMapState');
    if (!el) return;
    el.className = 'au-mapstate' + (s ? ' on' : '');
    if (s) el.textContent = s;
  }

  /** fetch + ONE retry. A cold NOAA proxy can lose a race with its own upstream
   *  lock and answer once with nothing; a blank map for the whole session is a
   *  much worse outcome than one extra request three seconds later. */
  function fetchJson(url, tries) {
    return fetch(url, { credentials: 'omit' })
      .then(function (r) { if (!r.ok) throw new Error('http ' + r.status); return r.json(); })
      .catch(function (err) {
        if ((tries || 0) >= 1) throw err;
        return new Promise(function (res) { setTimeout(res, 3000); })
          .then(function () { return fetchJson(url, (tries || 0) + 1); });
      });
  }

  function loadAurora(first) {
    if (first) setMapState(tr('loading_map'));
    return fetchJson(API.aurora)
      .then(function (d) {
        if (!d || !d.cells || !d.cells.length) throw new Error('empty');
        auroraData = d;
        buildGrid(d.cells);
        setMapState('');
        draw();
        renderMeta();
        recompute();
        if (first) stage('oval_ready', { cells: d.cells.length });
      })
      .catch(function () {
        if (!grid) setMapState(tr('err_map'));
        ae('tool_error', { slug: SLUG, where: 'aurora' });
      });
  }

  function loadNow(first) {
    return fetchJson(API.now)
      .then(function (d) {
        if (!d || d.err) throw new Error('err');
        nowData = d;
        renderKp();
        renderOutlook();
        renderAlerts();
        recompute();
        if (first) stage('kp_ready', {});
      })
      .catch(function () {
        var pick = $('auOutlookPick');
        if (pick && !nowData) { pick.hidden = false; pick.textContent = tr('err_kp'); }
        ae('tool_error', { slug: SLUG, where: 'now' });
      });
  }

  /** Cloud cover for the chosen point. Optional by design: a failure costs one
   *  line of the verdict, never the verdict. */
  function loadWeather(lat, lon) {
    weatherData = null;
    if (!API.weather) return;
    var mine = ++loadWeather._seq;
    fetch(API.weather + '?lat=' + lat.toFixed(3) + '&lon=' + lon.toFixed(3), { credentials: 'omit' })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        if (mine !== loadWeather._seq) return;      // user moved on
        if (d && d.hours && d.hours.length) { weatherData = d; recompute(); }
      })
      .catch(function () {});
  }
  loadWeather._seq = 0;

  /** Sky darkness (light pollution) at the point — the difference between a
   *  faint horizon glow being visible and being drowned by street lamps. */
  function loadSky(lat, lon) {
    skyData = null;
    if (!API.sky) return;
    var mine = ++loadSky._seq;
    fetch(API.sky + '?lat=' + lat.toFixed(3) + '&lon=' + lon.toFixed(3), { credentials: 'omit' })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        if (mine !== loadSky._seq) return;
        if (d && d.mpsas) { skyData = d; recompute(); }
      })
      .catch(function () {});
  }
  loadSky._seq = 0;

  // ════════════════════════════════════════════════════════════════════════
  //  6. Location
  // ════════════════════════════════════════════════════════════════════════
  function setLocation(lat, lon, name, source, tz) {
    lat = Math.max(-89.5, Math.min(89.5, lat));
    lon = ((lon + 180) % 360 + 360) % 360 - 180;
    // A map click carries no time zone of its own — but the shared gazetteer is
    // often already in memory from the search box, and it does.
    if (!tz && window.LocationInput && window.LocationInput.getData && window.LocationInput.getData()) {
      var near = window.LocationInput.nearestByCoords(lat, lon);
      if (near && near.tz) tz = near.tz;
    }
    loc = { lat: lat, lon: lon, name: name || null, tz: tz || null, src: source || null };
    setTimezone(tz, lon, source);
    placePin();
    loadWeather(lat, lon);
    loadSky(lat, lon);
    if (!name) reverseName(lat, lon);
    save();
    recompute();
    ae('feature_use', { slug: SLUG, f: 'locate', source: source || '' });
    stage('located', { source: source || '' });
  }

  /* Имя точки, выбранной на карте, — общий модуль GeoCity (public/js/geo-city.js):
     город с именем на языке страницы; дальше 25 км — со стрелкой и расстоянием. */
  function reverseName(lat, lon) {
    if (!window.GeoCity) return;
    var mine = ++reverseName._seq;
    GeoCity.lookup(lat, lon, { mode: 'near', far: true }).then(function (it) {
      if (mine !== reverseName._seq || !loc || !it || !it.found) return;
      loc.name = it.km > 25 ? GeoCity.text(it, { distance: true }) : it.name;
      save();
      renderVerdict();
    });
  }
  reverseName._seq = 0;

  function save() {
    try {
      window.__toolSettings && window.__toolSettings.save(SLUG, 'loc', loc);
      window.__toolSettings && window.__toolSettings.save(SLUG, 'layers', layers);
    } catch (e) {}
  }
  function restore() {
    try {
      var l = window.__toolSettings && window.__toolSettings.load(SLUG, 'loc', null);
      var ly = window.__toolSettings && window.__toolSettings.load(SLUG, 'layers', null);
      if (ly && typeof ly === 'object') {
        layers.night = ly.night !== false;
        layers.view = ly.view !== false;
      }
      if (l && typeof l.lat === 'number' && typeof l.lon === 'number') return l;
    } catch (e) {}
    return null;
  }

  function locateMe() {
    var b = $('auLocate');
    if (!navigator.geolocation) { if (window.__geo) window.__geo.notify('no_api'); return; }
    if (b) { b.disabled = true; b.classList.add('busy'); }
    navigator.geolocation.getCurrentPosition(function (pos) {
      if (b) { b.disabled = false; b.classList.remove('busy'); }
      setLocation(pos.coords.latitude, pos.coords.longitude, null, 'geo');
      if (map) map.setView([pos.coords.latitude, pos.coords.longitude], Math.max(map.getZoom(), 4), { animate: true });
    }, function (err) {
      if (b) { b.disabled = false; b.classList.remove('busy'); }
      if (window.__geo) window.__geo.notify(err);
    }, { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 });
  }

  // ── City search (shared /data/cities base via LocationInput) ──────────────
  var cityReady = false, cityFailed = false;
  function ensureCities() {
    if (cityReady || cityFailed || !window.LocationInput) return Promise.resolve(cityReady);
    return window.LocationInput.loadData().then(function () { cityReady = true; return true; })
      .catch(function () { cityFailed = true; return false; });
  }

  function wireSearch() {
    var inp = $('auSearch'), box = $('auResults');
    if (!inp || !box) return;
    var timer = null;

    function close() { box.innerHTML = ''; box.classList.remove('on'); inp.setAttribute('aria-expanded', 'false'); }
    function run() {
      var q = inp.value.trim();
      if (q.length < 2) { close(); return; }
      ensureCities().then(function (ok) {
        if (!ok || !window.LocationInput) {
          // A silent close here is a dead end: the user typed a city, nothing
          // happened, and nothing said why. The gazetteer is optional — the
          // map and «Near me» still work — so say that instead of failing mute.
          box.innerHTML = '';
          var e = document.createElement('div');
          e.className = 'au-res-empty';
          e.textContent = tr('err_cities');
          box.appendChild(e);
          box.classList.add('on');
          return;
        }
        window.LocationInput.searchWithFallback(q, 8).then(function (hits) {
          if (inp.value.trim() !== q) return;
          box.innerHTML = '';
          if (!hits.length) {
            var e = document.createElement('div');
            e.className = 'au-res-empty';
            e.textContent = tr('no_places');
            box.appendChild(e);
            box.classList.add('on');
            return;
          }
          hits.forEach(function (h) {
            var city = window.LocationInput.rowToCity(h.i);
            var btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'au-res';
            btn.setAttribute('role', 'option');
            var nm = document.createElement('span');
            nm.className = 'au-res-name'; nm.textContent = city.name;
            var meta = document.createElement('span');
            meta.className = 'au-res-meta';
            meta.textContent = [city.region, city.cc].filter(Boolean).join(', ');
            btn.appendChild(nm); btn.appendChild(meta);
            btn.addEventListener('click', function () {
              inp.value = city.name;
              close();
              setLocation(city.lat, city.lng, city.name, 'search', city.tz);
              if (map) map.setView([city.lat, city.lng], Math.max(map.getZoom(), 4), { animate: true });
            });
            box.appendChild(btn);
          });
          box.classList.add('on');
          inp.setAttribute('aria-expanded', 'true');
        });
      });
    }

    inp.addEventListener('focus', function () { ensureCities(); });
    inp.addEventListener('input', function () { clearTimeout(timer); timer = setTimeout(run, 120); });
    inp.addEventListener('keydown', function (ev) {
      var items = box.querySelectorAll('.au-res');
      if (ev.key === 'Escape') { close(); return; }
      if (!items.length) return;
      if (ev.key === 'ArrowDown') { ev.preventDefault(); items[0].focus(); }
      else if (ev.key === 'Enter') { ev.preventDefault(); items[0].click(); }
    });
    box.addEventListener('keydown', function (ev) {
      var items = Array.prototype.slice.call(box.querySelectorAll('.au-res'));
      var i = items.indexOf(document.activeElement);
      if (ev.key === 'ArrowDown') { ev.preventDefault(); if (items[i + 1]) items[i + 1].focus(); }
      else if (ev.key === 'ArrowUp') { ev.preventDefault(); if (i <= 0) inp.focus(); else items[i - 1].focus(); }
      else if (ev.key === 'Escape') { close(); inp.focus(); }
    });
    document.addEventListener('click', function (ev) {
      if (!box.contains(ev.target) && ev.target !== inp) close();
    });
  }

  // ════════════════════════════════════════════════════════════════════════
  //  7. The verdict — the whole point of the tool
  // ════════════════════════════════════════════════════════════════════════
  /** Strongest oval value in the poleward band that is still visible from here,
   *  plus how far away that patch is. */
  function horizonChance(lat, lon) {
    var hemi = lat >= 0 ? 1 : -1, best = 0, bestDist = null;
    for (var d = 0.5; d <= HORIZON_DEG; d += 0.5) {
      var v = 0;
      // Sample a small longitude fan: the aurora you see on the horizon is not
      // only the one due north of you.
      for (var dl = -6; dl <= 6; dl += 3) v = Math.max(v, sampleGrid(lat + hemi * d, lon + dl));
      if (v > best) { best = v; bestDist = d; }
    }
    return { value: best, deg: bestDist, km: bestDist ? Math.round(bestDist * 111.32) : null };
  }

  /** 15-minute sun/moon walk over the next 26 h — the honest way to get the
   *  dark window, because it needs no special case for polar day or night. */
  function skyWalk(lat, lon) {
    var out = [], start = new Date();
    start.setMinutes(Math.floor(start.getMinutes() / 15) * 15, 0, 0);
    for (var i = 0; i <= 104; i++) {
      var t = new Date(start.getTime() + i * 15 * 60000);
      out.push({ t: t, sun: sunAlt(t, lat, lon), moon: moonAlt(t, lat, lon) });
    }
    return out;
  }
  var DARK_DEG = -12;   // nautical twilight: the point a faint aurora survives

  function darkWindow(walk) {
    var minSun = 90, maxSun = -90, s = null, e = null;
    for (var i = 0; i < walk.length; i++) {
      if (walk[i].sun < minSun) minSun = walk[i].sun;
      if (walk[i].sun > maxSun) maxSun = walk[i].sun;
    }
    if (minSun >= DARK_DEG) return { kind: 'never', minSun: minSun, maxSun: maxSun };
    if (maxSun < DARK_DEG) return { kind: 'always', minSun: minSun, maxSun: maxSun };
    for (var j = 0; j < walk.length; j++) {
      if (walk[j].sun < DARK_DEG) { if (s === null) s = walk[j].t; }
      else if (s !== null) { e = walk[j].t; break; }
    }
    if (s === null) return { kind: 'never', minSun: minSun, maxSun: maxSun };
    return { kind: 'window', start: s, end: e, minSun: minSun, maxSun: maxSun };
  }

  function hhmm(d) {
    var x = atPlace(d);
    try { return x.toLocaleTimeString(C.locale, { hour: '2-digit', minute: '2-digit' }); }
    catch (e) { return x.toISOString().slice(11, 16); }
  }
  function dstr(d, opts) {
    var x = atPlace(d);
    try { return x.toLocaleString(C.locale, opts); }
    catch (e) { return x.toISOString().slice(0, 16).replace('T', ' '); }
  }
  /** Minutes past the hour at the PLACE — the timeline snaps to its clock. */
  function placeMinutes(d) { return atPlace(d).getMinutes(); }
  function isoAt(iso) { var t = Date.parse(String(iso || '').replace(' ', 'T').replace(/Z?$/, 'Z')); return isNaN(t) ? null : new Date(t); }

  /** Kp for a moment, from today's estimated rows plus the 3-day forecast. */
  function kpAt(date) {
    if (!nowData) return null;
    var ts = date.getTime(), rows = [];
    (nowData.estimated || []).forEach(function (r) { rows.push(r); });
    (nowData.forecast || []).forEach(function (r) { rows.push(r); });
    for (var i = 0; i < rows.length; i++) {
      var st = isoAt(rows[i].t);
      if (st && ts >= st.getTime() && ts < st.getTime() + 3 * 3600e3) return rows[i].kp;
    }
    return null;
  }
  function cloudAt(date) {
    if (!weatherData) return null;
    var ts = date.getTime(), best = null, bestD = 45 * 60000;
    for (var i = 0; i < weatherData.hours.length; i++) {
      var h = weatherData.hours[i];
      if (h.cloud == null) continue;
      var t = isoAt(h.t); if (!t) continue;
      var d = Math.abs(t.getTime() - ts);
      if (d < bestD) { bestD = d; best = h.cloud; }
    }
    return best;
  }

  function recompute() {
    if (!loc || !grid) { renderVerdict(); return; }
    var lat = loc.lat, lon = loc.lon;
    var overhead = sampleGrid(lat, lon);
    var hz = horizonChance(lat, lon);
    var walk = skyWalk(lat, lon);
    var dark = darkWindow(walk);
    var mlat = geomagLat(lat, lon);
    var kpOver = kpNeeded(mlat, 0);
    // Conservative 4°, not the full 9.1° geometric reach — why: geomag.js.
    var kpHoriz = kpNeeded(mlat, GM.HORIZON_MARGIN);

    // Moon during the dark window
    var moonUp = false, moonPeak = 0;
    var il = moonIllum(new Date());
    for (var i = 0; i < walk.length; i++) {
      var w = walk[i];
      if (w.sun >= DARK_DEG) continue;
      if (w.moon > 0) { moonUp = true; if (w.moon > moonPeak) moonPeak = w.moon; }
    }

    // Cloud + the best hour of the coming night
    var cloudAvg = null, best = null, samples = 0, sum = 0;
    // Solar midnight — the sun's lowest point of the walk. Auroral activity
    // peaks near magnetic midnight, and it is also the darkest moment, so ties
    // on cloud and Kp are broken toward it instead of toward "whichever sample
    // came first", which used to hand back the very start of twilight.
    var iMid = 0;
    for (var m = 1; m < walk.length; m++) if (walk[m].sun < walk[iMid].sun) iMid = m;

    for (var k = 0; k < walk.length; k++) {
      var p = walk[k];
      // Cloud is averaged over the dark window, or — where there is none (white
      // nights, polar day) — over the hours around solar midnight. Restricting
      // it to darkness made the tile read "no data for your spot" all summer at
      // exactly the latitudes this tool is for.
      var inCloudWindow = dark.kind === 'never' ? Math.abs(k - iMid) <= 12 : p.sun < DARK_DEG;
      var c = inCloudWindow || p.sun < DARK_DEG ? cloudAt(p.t) : null;
      if (inCloudWindow && c != null) { sum += c; samples++; }
      if (p.sun >= DARK_DEG) continue;
      var kp = kpAt(p.t);
      // Score the moment: clear sky first (it is binary in practice), then Kp,
      // then a penalty for a bright moon above the horizon, then the pull
      // toward solar midnight.
      var score = (c == null ? 50 : 100 - c)
        + (kp == null ? 0 : kp * 8)
        - (p.moon > 0 ? il.fraction * 18 : 0)
        + 14 * (1 - Math.min(1, Math.abs(k - iMid) / 24));
      if (!best || score > best.score) best = { t: p.t, score: score, cloud: c, kp: kp };
    }
    if (samples) cloudAvg = Math.round(sum / samples);

    lastVerdict = {
      overhead: overhead, horizon: hz, dark: dark, mlat: mlat,
      kpOver: kpOver, kpHoriz: kpHoriz, moonUp: moonUp, moonFrac: il.fraction,
      moonPhase: il.phase, cloudAvg: cloudAvg, best: best, walk: walk
    };
    renderVerdict();
    renderTimeline();
    renderOutlook();

    if (!completeSent) {
      completeSent = true;
      try {
        window.__aeToolComplete && window.__aeToolComplete({
          overhead: Math.round(overhead), horizon: Math.round(hz.value),
          kp_needed: kpOver, mlat: Math.round(mlat)
        });
      } catch (e) {}
    }
  }

  /** 0 = unlikely … 3 = likely. Drives colour, wording and the dot. */
  function verdictLevel(v) {
    if (v.dark.kind === 'never') return 0;
    if (v.overhead >= 20) return 3;
    if (v.overhead >= 7 || v.horizon.value >= 20) return 2;
    if (v.horizon.value >= 6) return 1;
    return 0;
  }

  function renderVerdict() {
    var empty = $('auEmpty'), body = $('auBody');
    if (!empty || !body) return;
    if (!loc) { empty.hidden = false; body.hidden = true; return; }
    empty.hidden = true; body.hidden = false;

    var placeEl = $('auPlace');
    if (placeEl) {
      placeEl.textContent = loc.name || (loc.lat.toFixed(2) + '°, ' + loc.lon.toFixed(2) + '°');
    }
    var coordEl = $('auCoords');
    if (coordEl) coordEl.textContent = loc.lat.toFixed(2) + '°, ' + loc.lon.toFixed(2) + '°';

    var v = lastVerdict;
    if (!v) {
      // A place is chosen but the oval has not landed yet. Say so in the card
      // rather than leaving four labelled em-dashes that read as "no aurora".
      var h0 = $('auHead');
      if (h0) {
        h0.className = 'au-head lvl0 wait';
        h0.querySelector('.au-head-title').textContent = tr('computing');
        h0.querySelector('.au-head-sub').textContent = tr('computing_sub');
      }
      var south0 = loc.lat < 0;
      setTile('auTileOverhead', tr('tile_overhead'), '…', '', '');
      setTile('auTileHorizon', fmt(tr('tile_horizon'), { dir: tr(south0 ? 'dir_south' : 'dir_north') }), '…', '', '');
      setTile('auTileDark', tr('tile_dark'), '…', '', '');
      setTile('auTileCloud', tr('tile_cloud'), '…', '', '');
      var thr0 = $('auThreshold'); if (thr0) thr0.hidden = true;
      var n0 = $('auNotes'); if (n0) n0.hidden = true;
      return;
    }
    var lvl = verdictLevel(v);
    var head = $('auHead');
    if (head) {
      head.className = 'au-head lvl' + lvl;
      head.querySelector('.au-head-title').textContent = tr(['v_none', 'v_low', 'v_maybe', 'v_likely'][lvl]);
      var sub = head.querySelector('.au-head-sub');
      sub.textContent = v.dark.kind === 'never'
        ? tr('v_sub_bright')
        : tr(['v_sub_none', 'v_sub_low', 'v_sub_maybe', 'v_sub_likely'][lvl]);
    }

    // ── the four tiles ──
    var south = loc.lat < 0;
    setTile('auTileOverhead', tr('tile_overhead'), Math.round(v.overhead) + '%',
      v.overhead >= 7 ? tr('tile_overhead_yes') : tr('tile_overhead_no'), v.overhead >= 7 ? 'good' : '');
    setTile('auTileHorizon', fmt(tr('tile_horizon'), { dir: tr(south ? 'dir_south' : 'dir_north') }),
      Math.round(v.horizon.value) + '%',
      v.horizon.km ? fmt(tr('tile_horizon_km'), { km: v.horizon.km }) : tr('tile_horizon_no'),
      v.horizon.value >= 6 ? 'good' : '');

    var darkLabel, darkHint;
    if (v.dark.kind === 'never') { darkLabel = '—'; darkHint = tr('dark_never'); }
    else if (v.dark.kind === 'always') { darkLabel = tr('dark_always_v'); darkHint = tr('dark_always'); }
    else { darkLabel = hhmm(v.dark.start); darkHint = v.dark.end ? fmt(tr('dark_until'), { time: hhmm(v.dark.end) }) : tr('dark_tonight'); }
    setTile('auTileDark', tr('tile_dark'), darkLabel, darkHint, v.dark.kind === 'never' ? 'bad' : 'good');

    var cloudTxt = v.cloudAvg == null ? '—' : v.cloudAvg + '%';
    var cloudHint = v.cloudAvg == null ? tr('cloud_unknown')
      : v.cloudAvg >= 80 ? tr('cloud_bad') : v.cloudAvg >= 45 ? tr('cloud_mixed') : tr('cloud_good');
    setTile('auTileCloud', tr('tile_cloud'), cloudTxt, cloudHint,
      v.cloudAvg == null ? '' : v.cloudAvg >= 80 ? 'bad' : v.cloudAvg < 45 ? 'good' : '');

    // ── the personal Kp line ──
    var thr = $('auThreshold');
    if (thr) {
      thr.hidden = false;
      var kpNow = nowData && nowData.kp && nowData.kp.now != null ? nowData.kp.now : null;
      var parts = [];
      if (v.kpOver == null && v.kpHoriz == null) {
        parts.push(fmt(tr('kp_too_far'), { mlat: Math.abs(v.mlat).toFixed(1) }));
      } else if (Math.abs(v.mlat) >= GM.KP_BOUNDARY[0]) {
        parts.push(tr('kp_inside_oval'));
      } else if (v.kpHoriz === 0) {
        // "a glow shows at Kp ≥ 0" is a true statement that reads like a bug.
        parts.push(fmt(tr('kp_need_any'), {
          over: v.kpOver == null ? '9+' : v.kpOver,
          mlat: Math.abs(v.mlat).toFixed(1)
        }));
      } else {
        parts.push(fmt(tr('kp_need'), {
          over: v.kpOver == null ? '9+' : v.kpOver,
          horiz: v.kpHoriz == null ? '9+' : v.kpHoriz,
          mlat: Math.abs(v.mlat).toFixed(1)
        }));
      }
      if (kpNow != null) parts.push(fmt(tr('kp_now_is'), { kp: kpNow % 1 ? kpNow.toFixed(1) : kpNow }));
      thr.innerHTML = '';
      var strong = document.createElement('span');
      strong.className = 'au-thr-main';
      strong.textContent = parts[0];
      thr.appendChild(strong);
      if (parts[1]) {
        var s2 = document.createElement('span');
        s2.className = 'au-thr-now';
        s2.textContent = parts[1];
        thr.appendChild(s2);
      }
    }

    // ── extra context: moon, sky darkness, where to look ──
    var notes = $('auNotes');
    if (notes) {
      notes.hidden = false;
      notes.innerHTML = '';
      var add = function (icon, text) {
        if (!text) return;
        var li = document.createElement('li');
        var i = document.createElement('span'); i.className = 'au-note-ico'; i.textContent = icon;
        var s = document.createElement('span'); s.textContent = text;
        li.appendChild(i); li.appendChild(s); notes.appendChild(li);
      };
      // Where the oval is overhead the advice "look at the horizon" is wrong —
      // the display is above you and people miss it looking the wrong way.
      add('🧭', v.overhead >= 7
        ? fmt(tr('note_look_up'), { dir: tr(south ? 'dir_south_look' : 'dir_north_look') })
        : fmt(tr('note_look'), { dir: tr(south ? 'dir_south_look' : 'dir_north_look') }));
      // With no dark window at all the moon is beside the point, and "the sky
      // stays dark" would flatly contradict the tile that just said it will not
      // get dark today.
      if (v.dark.kind !== 'never') {
        var pct = Math.round(v.moonFrac * 100);
        // Below roughly a quarter lit the moon adds almost nothing to sky
        // brightness, so "it will wash out a faint glow" beside "the moon is 1%"
        // is a sentence that reads as a bug even though the geometry is right.
        var moonKey = !v.moonUp ? 'note_moon_down'
          : (v.moonFrac < 0.25 ? 'note_moon_up_dim' : 'note_moon_up');
        add(moonEmoji(v.moonPhase), fmt(tr(moonKey), { pct: pct }));
      }
      if (skyData && skyData.mpsas) {
        var m = skyData.mpsas;
        var skyKey = m >= 21.6 ? 'sky_dark' : m >= 20.8 ? 'sky_rural' : m >= 19.5 ? 'sky_suburb' : 'sky_city';
        add('🌃', fmt(tr('note_sky'), { label: tr(skyKey), val: m.toFixed(1) }));
      }
      if (v.best && v.dark.kind !== 'never') {
        add('⭐', fmt(tr('note_best'), { time: hhmm(v.best.t) }));
      }
      // Only worth a line when it is NOT the reader's own clock — otherwise it
      // is noise on the common case.
      if (tzDiffersFromBrowser()) add('🕒', fmt(tr('note_tz'), { tz: tzLabel() }));
    }
  }

  function setTile(id, label, value, hint, tone) {
    var el = $(id); if (!el) return;
    el.className = 'au-tile' + (tone ? ' ' + tone : '');
    el.querySelector('.au-tile-label').textContent = label;
    el.querySelector('.au-tile-value').textContent = value;
    el.querySelector('.au-tile-hint').textContent = hint;
  }

  // ════════════════════════════════════════════════════════════════════════
  //  8. Tonight timeline
  // ════════════════════════════════════════════════════════════════════════
  function renderTimeline() {
    var host = $('auHours'), card = $('auTonight');
    if (!host || !card || !lastVerdict || !loc) return;
    var v = lastVerdict;
    if (v.dark.kind === 'never') {
      card.hidden = false;
      host.innerHTML = '';
      var p = document.createElement('p');
      p.className = 'au-hours-empty';
      p.textContent = tr('hours_polar_day');
      host.appendChild(p);
      return;
    }
    card.hidden = false;

    // Window: from an hour before darkness (or now, whichever is later) to an
    // hour after it lifts — capped at 16 cells so it stays legible on a phone.
    var startT, endT;
    if (v.dark.kind === 'always') {
      startT = new Date(); endT = new Date(Date.now() + 12 * 3600e3);
    } else {
      startT = new Date(Math.max(Date.now(), v.dark.start.getTime() - 3600e3));
      endT = v.dark.end ? new Date(v.dark.end.getTime() + 3600e3) : new Date(startT.getTime() + 10 * 3600e3);
    }
    // Snap to the PLACE's hour boundaries, not the browser's: in a half-hour
    // zone (India, South Australia) the browser's boundaries land on :30 there.
    var hours = [];
    var t = new Date(startT.getTime() + ((60 - placeMinutes(startT)) % 60) * 60000);
    t = new Date(Math.floor(t.getTime() / 60000) * 60000);
    while (t <= endT && hours.length < 16) { hours.push(new Date(t)); t = new Date(t.getTime() + 3600e3); }
    // A two-cell strip reads as a bug; six is the floor. Anything past the dark
    // window is padding, so do not pad further than that.
    while (hours.length < 6) { hours.push(new Date(t)); t = new Date(t.getTime() + 3600e3); }

    host.innerHTML = '';
    var maxKp = 1;
    hours.forEach(function (h) { var k = kpAt(h); if (k != null && k > maxKp) maxKp = k; });
    maxKp = Math.max(maxKp, (lastVerdict.kpHoriz || 0) + 1, 4);

    hours.forEach(function (h) {
      var sun = sunAlt(h, loc.lat, loc.lon);
      var kp = kpAt(h), cloud = cloudAt(h);
      var dark = sun < DARK_DEG;
      var cell = document.createElement('button');
      cell.type = 'button';
      cell.className = 'au-hour' + (dark ? ' dark' : '') + (previewTime && Math.abs(previewTime - h) < 1800e3 ? ' sel' : '');
      cell.setAttribute('aria-pressed', cell.classList.contains('sel') ? 'true' : 'false');

      var lbl = document.createElement('span');
      lbl.className = 'au-hour-t';
      lbl.textContent = hhmm(h);

      var bar = document.createElement('span');
      bar.className = 'au-hour-bar';
      var fill = document.createElement('i');
      fill.style.height = Math.max(5, Math.min(100, (kp == null ? 0 : kp / maxKp * 100))) + '%';
      if (kp != null && lastVerdict.kpHoriz && kp >= lastVerdict.kpHoriz) fill.className = 'hit';
      bar.appendChild(fill);

      var kpTxt = document.createElement('span');
      kpTxt.className = 'au-hour-kp';
      kpTxt.textContent = kp == null ? '—' : (kp % 1 ? kp.toFixed(1) : kp);

      var cl = document.createElement('span');
      cl.className = 'au-hour-cloud';
      cl.textContent = cloud == null ? '' : Math.round(cloud) + '%';
      if (cloud != null && cloud >= 80) cl.classList.add('bad');
      else if (cloud != null && cloud < 45) cl.classList.add('good');

      cell.appendChild(lbl); cell.appendChild(bar); cell.appendChild(kpTxt); cell.appendChild(cl);
      cell.title = tr('hour_hint');
      cell.addEventListener('click', function () {
        previewTime = (previewTime && Math.abs(previewTime - h) < 1800e3) ? null : h;
        draw();
        renderTimeline();
        renderMeta();
        ae('feature_use', { slug: SLUG, f: 'scrub' });
      });
      host.appendChild(cell);
    });

    var legend = $('auHoursNote');
    if (legend) {
      legend.textContent = lastVerdict.kpHoriz
        ? fmt(tr('hours_note_kp'), { kp: lastVerdict.kpHoriz })
        : tr('hours_note');
    }
  }

  // ════════════════════════════════════════════════════════════════════════
  //  9. Kp badge, 3-day outlook, alerts, timestamps
  // ════════════════════════════════════════════════════════════════════════
  function renderKp() {
    var badge = $('auKp');
    if (!badge || !nowData) return;
    var kp = (nowData.kp && nowData.kp.now != null) ? nowData.kp.now : 0;
    var storm = !!nowData.verdict, g = (nowData.storm && nowData.storm.g) || 0;
    badge.innerHTML = '';
    var b = document.createElement('b');
    b.textContent = 'Kp ' + (kp % 1 ? kp.toFixed(1) : kp);
    var s = document.createElement('span');
    s.textContent = storm ? (tr('storm_on') + ' G' + g) : tr('storm_off');
    badge.appendChild(b); badge.appendChild(s);
    badge.className = 'au-kp ' + (kp >= 6 ? 'hi' : kp >= 5 ? 'mid' : kp >= 4 ? 'lo' : '');
  }

  function renderMeta() {
    var el = $('auTime');
    if (!el) return;
    var bits = [];
    if (auroraData && auroraData.fcst) bits.push(tr('forecast_for') + ' ' + hhmm(isoAt(auroraData.fcst) || new Date()));
    if (previewTime) bits.push(fmt(tr('preview_at'), { time: hhmm(previewTime) }));
    el.textContent = bits.join(' · ');
    var reset = $('auReset');
    if (reset) reset.hidden = !previewTime;
  }

  function renderOutlook() {
    var host = $('auOutlook');
    if (!host || !nowData) return;
    var rows = [];
    (nowData.kpHistory || []).slice(-8).forEach(function (x) { rows.push({ kp: x.kp, t: x.t, p: false }); });
    (nowData.estimated || []).forEach(function (x) {
      var st = isoAt(x.t);
      if (st && st.getTime() <= Date.now()) rows.push({ kp: x.kp, t: x.t, p: false });
    });
    (nowData.forecast || []).forEach(function (x) { rows.push({ kp: x.kp, t: x.t, p: true }); });
    // de-dupe on time (history and today's estimates overlap by construction)
    var seen = {}, all = [];
    rows.forEach(function (r) { if (!seen[r.t]) { seen[r.t] = 1; all.push(r); } });
    all.sort(function (a, b) { return String(a.t).localeCompare(String(b.t)); });
    all = all.slice(-32);
    if (!all.length) { host.parentNode.hidden = true; return; }

    host.innerHTML = '';
    // A threshold of 0 means the oval reaches this place at ANY Kp — drawing a
    // line along the floor and lighting up every single bar as "your night"
    // says nothing. Inside the oval the chart goes back to being just a chart.
    var thr = (lastVerdict && lastVerdict.kpHoriz) ? lastVerdict.kpHoriz : null;
    var insideOval = !!(lastVerdict && lastVerdict.kpHoriz === 0);

    var wrap = document.createElement('div');
    wrap.className = 'au-bars';
    // Kp is a fixed 0–9 scale, so on a quiet week every bar is short and the
    // chart says nothing on its own. The G1 line is the reference that gives
    // the height a meaning.
    var g1 = document.createElement('div');
    g1.className = 'au-g1-line';
    g1.style.bottom = (5 / 9 * 100) + '%';
    var g1t = document.createElement('span');
    g1t.textContent = tr('outlook_g1');
    g1.appendChild(g1t);
    wrap.appendChild(g1);
    // The line lives INSIDE the bar box so its percentage is measured against
    // exactly the same height the bars are — no padding to reason about.
    if (thr != null && thr <= 9) {
      var line = document.createElement('div');
      line.className = 'au-thr-line';
      line.style.bottom = (thr / 9 * 100) + '%';
      var tag = document.createElement('span');
      tag.textContent = fmt(tr('outlook_you'), { kp: thr });
      line.appendChild(tag);
      wrap.appendChild(line);
    }
    var lastDay = '';
    all.forEach(function (b) {
      var cell = document.createElement('div');
      cell.className = 'au-bar-wrap';
      var bar = document.createElement('div');
      bar.className = 'au-bar' + (b.p ? ' pred' : '') + (thr != null && b.kp >= thr ? ' hit' : '');
      bar.style.height = Math.max(4, (b.kp / 9) * 100) + '%';
      var col = b.kp >= 6 ? '#ff4d6d' : b.kp >= 5 ? '#ffd43b' : b.kp >= 4 ? '#94c93d' : '#36d399';
      if (b.p) { bar.style.borderColor = col; bar.style.color = col; } else bar.style.background = col;
      var when = isoAt(b.t);
      bar.title = 'Kp ' + (b.kp % 1 ? b.kp.toFixed(1) : b.kp) + (when ? ' · ' + dstr(when, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');
      cell.appendChild(bar);
      // one date label per day
      if (when) {
        var day = dstr(when, { day: 'numeric', month: 'short' });
        if (day !== lastDay) {
          lastDay = day;
          var lab = document.createElement('span');
          lab.className = 'au-bar-day';
          lab.textContent = day;
          cell.appendChild(lab);
        }
      }
      wrap.appendChild(cell);
    });
    host.appendChild(wrap);

    // "the next night that clears your bar"
    var pick = $('auOutlookPick');
    if (pick) {
      if (insideOval) {
        pick.hidden = false;
        pick.className = 'au-outlook-pick on';
        pick.textContent = tr('outlook_always');
      } else if (thr == null) { pick.hidden = true; }
      else {
        // A "window" is a forecast block that clears the threshold AND happens
        // while the sky at this place is dark. Without the second half the tool
        // cheerfully recommended 09:30 in the morning.
        var next = null;
        for (var i = 0; i < all.length; i++) {
          if (!all[i].p || all[i].kp < thr) continue;
          var w = isoAt(all[i].t);
          if (!w || w.getTime() <= Date.now()) continue;
          var mid = new Date(w.getTime() + 90 * 60000);
          if (loc && sunAlt(mid, loc.lat, loc.lon) >= DARK_DEG) continue;
          next = { t: w, kp: all[i].kp };
          break;
        }
        pick.hidden = false;
        pick.className = 'au-outlook-pick' + (next ? ' on' : '');
        pick.textContent = next
          ? fmt(tr('outlook_next'), {
              when: dstr(next.t, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }),
              kp: next.kp % 1 ? next.kp.toFixed(1) : next.kp
            })
          : fmt(tr('outlook_none'), { kp: thr });
      }
    }
  }

  function renderAlerts() {
    var host = $('auAlerts');
    if (!host || !nowData) return;
    var rows = (nowData.alerts || []).filter(function (a) {
      return a.family === 'kp' && (a.kind === 'alert' || a.kind === 'warning' || a.kind === 'watch');
    }).slice(0, 2);
    host.innerHTML = '';
    if (!rows.length) { host.hidden = true; return; }
    host.hidden = false;
    rows.forEach(function (a) {
      var el = document.createElement('div');
      el.className = 'au-alert';
      var ic = document.createElement('span'); ic.className = 'au-alert-ico'; ic.textContent = '⚡';
      var tx = document.createElement('span');
      var when = a.valid_from ? isoAt(a.valid_from) : (a.issued ? isoAt(a.issued) : null);
      tx.textContent = fmt(tr('alert_kp'), {
        kind: tr('alert_' + a.kind),
        k: a.k != null ? a.k : '5',
        g: a.level != null ? ('G' + a.level) : '',
        when: when ? dstr(when, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''
      }).replace(/\s+/g, ' ').trim();
      el.appendChild(ic); el.appendChild(tx);
      host.appendChild(el);
    });
  }

  // ════════════════════════════════════════════════════════════════════════
  // 10. Controls
  // ════════════════════════════════════════════════════════════════════════
  function wireControls() {
    var lb = $('auLocate'); if (lb) lb.addEventListener('click', locateMe);
    var change = $('auChange');
    if (change) change.addEventListener('click', function () {
      var s = $('auSearch');
      if (s) { s.focus(); s.select(); }
    });
    var reset = $('auReset');
    if (reset) reset.addEventListener('click', function () {
      previewTime = null; draw(); renderTimeline(); renderMeta();
    });
    ['night', 'view'].forEach(function (key) {
      var el = $('auLayer' + key.charAt(0).toUpperCase() + key.slice(1));
      if (!el) return;
      el.checked = layers[key];
      el.addEventListener('change', function () {
        layers[key] = el.checked;
        save(); draw();
        ae('feature_use', { slug: SLUG, f: 'layer_' + key, on: el.checked ? 1 : 0 });
      });
    });
    document.querySelectorAll('[data-au-hemi]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var h = btn.getAttribute('data-au-hemi') === 's' ? -1 : 1;
        if (map) map.setView([h * 62, map.getCenter().lng], 3, { animate: true });
        document.querySelectorAll('[data-au-hemi]').forEach(function (b) { b.classList.toggle('on', b === btn); });
        ae('feature_use', { slug: SLUG, f: 'hemi', h: h });
      });
    });
    var legend = $('auLegendToggle');
    if (legend) legend.addEventListener('click', function () {
      var body = $('auLegend');
      if (!body) return;
      var open = body.classList.toggle('on');
      legend.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  // ════════════════════════════════════════════════════════════════════════
  // 11. Boot
  // ════════════════════════════════════════════════════════════════════════
  function boot() {
    stage('view', {});
    initMap();
    wireControls();
    wireSearch();

    // A signed-in user with a city on their profile gets it applied to every
    // location tool — the shared contract documented in location-input.js. The
    // tool's own last-used place still wins when there is one.
    var saved = restore();
    if (!saved && window.__USER_LOCATION && window.__USER_LOCATION.lat != null) {
      var u = window.__USER_LOCATION;
      saved = { lat: u.lat, lon: u.lng, name: u.cityName || null, tz: u.tz || null, src: 'profile' };
    }
    ['night', 'view'].forEach(function (k) {
      var el = $('auLayer' + k.charAt(0).toUpperCase() + k.slice(1));
      if (el) el.checked = layers[k];
    });

    Promise.all([loadAurora(true), loadNow(true)]).then(function () {
      if (saved) {
        loc = saved;
        setTimezone(saved.tz, saved.lon, saved.src);
        placePin();
        loadWeather(saved.lat, saved.lon);
        loadSky(saved.lat, saved.lon);
        if (map) map.setView([saved.lat, saved.lon], Math.max(map.getZoom(), 4));
        var s = $('auSearch');
        if (s && saved.name) s.value = saved.name;
        recompute();
        stage('restored', {});
      }
    });

    // The oval nowcast refreshes every ~2.5 min upstream; Kp every minute.
    setInterval(function () { loadAurora(false); }, 150000);
    setInterval(function () { loadNow(false); }, 60000);
    // Darkness moves even when nothing is fetched.
    setInterval(function () { if (!previewTime) { draw(); if (loc) recompute(); } }, 300000);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
